<template>
    <div class="analysis-container">
        <!-- 상단 테이블 -->
        <div class="analysis-table-container">
            <div class="table-content">
                <div class="table-row header-row">
                    <div class="table-cell header-cell">달성 월</div>
                    <div class="table-cell header-cell">미달성 월</div>
                    <div class="table-cell header-cell">목표 대비</div>
                </div>
                <div class="table-row data-row">
                    <div class="table-cell data-cell achievement">
                        <span class="achievement-count">{{ achievementMonths }}</span>
                        <span class="achievement-text">개월</span>
                    </div>
                    <div class="table-cell data-cell underachievement">
                        <span class="underachievement-count">{{ underachievementMonths }}</span>
                        <span class="underachievement-text">개월</span>
                    </div>
                    <div class="table-cell data-cell difference">
                        <span class="difference-value" :class="differenceColorClass">{{ differenceSign }}{{
                            totalDifference }}</span>
                        <span class="difference-text">{{ mainUnit }}</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- 하단 바차트 -->
        <div class="analysis-chart-container">
            <div ref="barChart" class="bar-chart"></div>
        </div>
    </div>
</template>

<script>
import * as echarts from "echarts";

export default {
    name: "xeGoalBeforeAnalysis",
    props: ["data", "cardSize"],
    data() {
        return {
            barChart: null,
            monthlyData: [], // 월별 데이터
        };
    },
    computed: {
        currentMonth() {
            // 현재 월을 가져오기 (1-12)
            const now = new Date();
            return now.getMonth() + 1;
        },
        previousMonth() {
            // 이전 월 계산
            return this.currentMonth - 1;
        },
        achievementMonths() {
            // 이전월까지만 달성월 카운트 (실제 데이터가 있는 것만)
            return this.monthlyData.filter(
                (item) => item.hasData && item.isAchieved && item.monthIndex <= this.previousMonth
            ).length;
        },
        underachievementMonths() {
            // 이전월까지만 미달성월 카운트 (실제 데이터가 있는 것만)
            return this.monthlyData.filter(
                (item) => item.hasData && !item.isAchieved && item.monthIndex <= this.previousMonth
            ).length;
        },
        totalDifference() {
            // 이전월까지만 실제 차이값들의 합계 (실제 데이터가 있는 것만)

            const filteredData = this.monthlyData.filter(
                (item) => item.hasData && item.monthIndex <= this.previousMonth
            );

            const totalValue = filteredData.reduce((sum, item) => {
                return sum + item.difference;
            }, 0);

            const formattedValue = this.formatValue(totalValue, 0);
            return formattedValue;
        },
        mainUnit() {
            if (!this.data || !this.data.headers) return "";
            const mainHeader = this.data.headers.find((header) => header.roleType === "Main");
            return mainHeader ? mainHeader.unitSmallCode : "";
        },
        differenceColorClass() {
            const filteredData = this.monthlyData.filter(
                (item) => item.hasData && item.monthIndex <= this.previousMonth
            );
            const totalValue = filteredData.reduce((sum, item) => {
                return sum + item.difference;
            }, 0);

            if (totalValue > 0) {
                return "difference-positive"; // 초과 사용 (붉은색)
            } else if (totalValue < 0) {
                return "difference-neutral"; // 목표 달성 (푸른색)
            } else {
                return "difference-negative"; // 목표 달성 (푸른색)
            }
        },
        differenceSign() {
            const filteredData = this.monthlyData.filter(
                (item) => item.hasData && item.monthIndex <= this.previousMonth
            );
            const totalValue = filteredData.reduce((sum, item) => {
                return sum + item.difference;
            }, 0);

            if (totalValue > 0) {
                return "+"; // 초과 사용
            } else if (totalValue < 0) {
                return ""; // 목표 달성 (음수는 이미 - 부호가 있음)
            } else {
                return ""; // 0인 경우
            }
        },
    },
    watch: {
        cardSize: {
            handler(newSize, oldSize) {
                console.log("cardSize changed: ", newSize);
                if (newSize !== oldSize) {
                    this.renderBarChart();
                }
            },
        },
        data: {
            handler(newData) {
                if (newData && newData.values) {
                    this.processApiData(newData);
                }
            },
            immediate: true,
            deep: true,
        },
        monthlyData: {
            handler(newData) {
                // 데이터가 없으면 차트 렌더링하지 않음
                if (!newData || newData.length === 0) {
                    return;
                }

                this.$nextTick(() => {
                    this.renderBarChart();
                });
            },
            deep: true,
        },
    },
    mounted() {
        // props 데이터가 있으면 차트 렌더링
        this.$nextTick(() => {
            if (this.monthlyData && this.monthlyData.length > 0) {
                this.renderBarChart();
            }
        });
    },
    beforeDestroy() {
        if (this.barChart) {
            this.barChart.dispose();
            this.barChart = null;
        }
        window.removeEventListener("resize", () => {
            this.barChart.resize();
        });
    },
    methods: {
        formatValue(value, toFixedValue) {
            // value가 유효한 숫자인지 확인
            const numValue = Number(value);
            if (isNaN(numValue)) {
                return "0";
            }
            let localValue = numValue;
            const isNegative = localValue < 0;
            const absValue = Math.abs(localValue);

            // 단위 변환
            let result = "";
            if (absValue >= 1e9) {
                const gValue = absValue / 1e9;
                if (toFixedValue !== undefined && toFixedValue !== null) {
                    result = gValue.toFixed(toFixedValue) + "G";
                } else {
                    result = Number.isInteger(gValue) ? gValue + "G" : gValue.toFixed(1) + "G";
                }
            } else if (absValue >= 1e6) {
                const mValue = absValue / 1e6;
                if (toFixedValue !== undefined && toFixedValue !== null) {
                    result = mValue.toFixed(toFixedValue) + "M";
                } else {
                    result = Number.isInteger(mValue) ? mValue + "M" : mValue.toFixed(1) + "M";
                }
            } else if (absValue >= 1e3) {
                const kValue = absValue / 1e3;
                if (toFixedValue !== undefined && toFixedValue !== null) {
                    result = kValue.toFixed(toFixedValue) + "K";
                } else {
                    result = Number.isInteger(kValue) ? kValue + "K" : kValue.toFixed(1) + "K";
                }
            } else {
                result = Number.isInteger(absValue) ? absValue.toString() : absValue.toFixed(1);
            }

            const finalResult = isNegative ? "-" + result : result;
            return finalResult;
        },

        processApiData(apiData) {
            // headers에서 Goal과 Main 데이터 찾기
            const goalHeader = apiData.headers.find((header) => header.roleType === "Goal");
            const mainHeader = apiData.headers.find((header) => header.roleType === "Main");

            if (!goalHeader || !mainHeader) {
                console.error("Goal or Main header not found");
                return;
            }

            const goalKey = goalHeader.key;
            const mainKey = mainHeader.key;

            // values에서 데이터 추출
            const goalValues = apiData.values[goalKey] || [];
            const mainValues = apiData.values[mainKey] || [];
            const regDt = apiData.values.regDt || [];

            // 월별 데이터 생성
            this.monthlyData = [];

            for (let i = 0; i < Math.min(12, goalValues.length, mainValues.length, regDt.length); i++) {
                // mainValue가 null, undefined, 0인지 체크
                const mainRawValue = mainValues[i];
                const hasMainData = mainRawValue !== null && mainRawValue !== undefined && Number(mainRawValue) !== 0;

                // 실제 사용할 값 (null/undefined/0이면 0으로 처리)
                const goalValue = goalValues[i] !== null && goalValues[i] !== undefined ? Number(goalValues[i]) || 0 : 0;
                const mainValue = hasMainData ? Number(mainRawValue) : 0;
                const regDate = regDt[i] || "";

                // regDt에서 월 추출 (예: "2025-01" -> "1월")
                let monthLabel = "";
                if (regDate) {
                    const monthMatch = regDate.match(/-(\d{2})$/);
                    if (monthMatch) {
                        const monthNum = parseInt(monthMatch[1], 10);
                        monthLabel = `${monthNum}월`;
                    } else {
                        monthLabel = `${i + 1}월`; // fallback
                    }
                } else {
                    monthLabel = `${i + 1}월`; // fallback
                }

                // 절대적인 차이 계산 (Main - Goal)
                const difference = mainValue - goalValue;
                const isAchieved = difference <= 0; // 목표 달성 여부 (사용량이 목표보다 적으면 달성)

                this.monthlyData.push({
                    month: monthLabel,
                    monthIndex: i + 1,
                    goalUsage: goalValue,
                    actualUsage: mainValue,
                    difference: difference, // 절대적인 차이
                    isAchieved: isAchieved, // 달성 여부
                    hasData: hasMainData, // 실제 데이터 존재 여부
                });
            }
        },

        getQuarterLines() {
            // 분기 시작월에 세로선 추가 (1월, 4월, 7월, 10월)
            const quarterLines = [];
            const quarterMonths = [1, 4, 7, 10]; // 분기 시작월

            quarterMonths.forEach((monthNum) => {
                const monthLabel = `${monthNum}월`;
                const monthIndex = this.monthlyData.findIndex((item) => item.month === monthLabel);

                if (monthIndex !== -1) {
                    quarterLines.push({
                        xAxis: monthIndex,
                        lineStyle: {
                            color: "#e0e0e0",
                            type: "solid",
                            width: 1,
                        },
                    });
                }
            });

            return quarterLines;
        },

        renderBarChart() {
            // DOM 요소가 존재하는지 확인
            if (!this.$refs.barChart) {
                console.error("barChart ref not found");
                return;
            }

            // 데이터가 있는지 확인
            if (!this.monthlyData || this.monthlyData.length === 0) {
                console.error("monthlyData is empty");
                return;
            }

            if (this.barChart) {
                this.barChart.dispose();
            }

            this.barChart = echarts.init(this.$refs.barChart);

            const months = this.monthlyData.map((item) => item.month);

            // 이전월까지만 실제 데이터 사용, 데이터가 없는 항목은 0으로 처리
            const differences = this.monthlyData.map((item) => {
                // 데이터가 없거나 당월 이후면 0 반환
                if (!item.hasData || item.monthIndex > this.previousMonth) {
                    return 0;
                }
                return item.difference;
            });

            // 양방향 바차트를 위한 데이터 변환
            const barData = differences.map((diff) => {
                return {
                    value: Math.abs(diff), // 절댓값
                    direction: diff >= 0 ? "up" : "down", // 방향 (양수면 위로, 음수면 아래로)
                    originalDiff: diff,
                    color: diff >= 0 ? "#f44336" : "#4caf50", // 양수면 빨간색, 음수면 초록색
                };
            });

            const option = {
                grid: {
                    top: "12%",
                    left: "3%",
                    right: "4%",
                    height: "88%",
                    containLabel: true,
                },
                tooltip: {
                    confine: false,
                    appendToBody: true,
                    trigger: "axis",
                    backgroundColor: "rgba(0,0,0,0.8)",
                    borderWidth: 0,
                    borderColor: "transparent",
                    textStyle: {
                        color: "#fff",
                        fontSize: 12,
                    },
                    axisPointer: {
                        type: "cross",
                        label: {
                            backgroundColor: "#6a7985",
                        },
                    },
                    formatter: (params) => {
                        const data = params[0];
                        const monthData = this.monthlyData[data.dataIndex];
                        const barInfo = barData[data.dataIndex];

                        return `
                            <div style="padding: 5px; border: none !important; outline: none !important; box-shadow: none !important; background: none; display: flex; flex-direction: column; align-items: flex-start;">
                                <div style="font-weight: bold; margin-bottom: 8px;">${monthData.month}</div>
                                <div style="margin-bottom: 4px;">
                                    <span>목표 : </span> 
                                    <span style="font-weight: bold;">${this.formatValue(monthData.goalUsage)}</span>
                                </div>
                                <div style="margin-bottom: 4px;">
                                    <span>실제 : </span> 
                                    <span style="font-weight: bold;">${this.formatValue(monthData.actualUsage)}</span>
                                </div>
                                <div>
                                    <span>차이 : </span> 
                                    <span style="font-weight: bold; color: ${barInfo.color};">
                                        ${barInfo.originalDiff >= 0 ? "+" : ""}${this.formatValue(Number(barInfo.originalDiff))}
                                    </span>
                                </div>
                               
                            </div>
                        `;
                    },
                },
                xAxis: {
                    type: "category",
                    data: months,
                    axisLabel: {
                        fontSize: 10,
                        color: "#666",
                        interval: 0, // 모든 라벨 표시
                        formatter: (value) => {
                            // 분기 시작월만 표시 (1월, 4월, 7월, 10월)
                            const monthStr = value.replace("월", "");
                            const monthNum = parseInt(monthStr);
                            if (monthNum === 1 || monthNum === 4 || monthNum === 7 || monthNum === 10) {
                                return value;
                            }
                            return "";
                        },
                    },
                    axisLine: {
                        lineStyle: {
                            color: "#ddd",
                        },
                    },
                    axisTick: {
                        show: false,
                    },
                },

                yAxis: {
                    type: "value",
                    name: "",
                    nameTextStyle: {
                        color: "#666",
                        fontSize: 12,
                    },
                    axisLabel: {
                        color: "#666",
                        fontSize: 10,
                        formatter: (value) => {
                            return this.formatValue(value);
                        },
                    },
                    axisLine: {
                        lineStyle: {
                            color: "#ddd",
                        },
                    },
                    splitLine: {
                        lineStyle: {
                            color: "#f0f0f0",
                        },
                    },
                    // 기준선 (0) 표시
                    axisTick: {
                        show: false,
                    },
                },
                series: [
                    {
                        name: "사용량 차이",
                        type: "bar",
                        data: barData.map((item) => {
                            return {
                                value: item.direction === "up" ? item.value : -item.value, // 양수면 위로, 음수면 아래로
                                itemStyle: {
                                    color: item.color,
                                    borderRadius: item.direction === "up" ? [20, 20, 0, 0] : [0, 0, 20, 20],
                                },
                            };
                        }),
                        barWidth: "60%",

                        label: {
                            show: false,
                            position: (params) => {
                                const data = barData[params.dataIndex];
                                return data.direction === "up" ? "top" : "bottom";
                            },
                            formatter: (params) => {
                                const data = barData[params.dataIndex];
                                const value = data.originalDiff;
                                return (value >= 0 ? "+" : "") + this.formatValue(value);
                            },
                            fontSize: 10,
                            color: "#666",
                        },
                        // 분기 구분선 추가
                        markLine: {
                            symbol: "none",
                            lineStyle: {
                                color: "#e0e0e0",
                                type: "solid",
                                width: 1,
                            },
                            label: {
                                show: false,
                            },
                            data: this.getQuarterLines(),
                            silent: true,
                            z: 0,
                        },
                    },
                    // 기준선 (0) 표시
                    {
                        name: "기준선",
                        type: "line",
                        symbol: "none",
                        lineStyle: {
                            color: "#999",
                            type: "dashed",
                            width: 1,
                        },
                        data: months.map(() => 0), // 0 기준선
                        silent: true,
                        z: 1,
                    },
                ],
            };

            this.barChart.setOption(option);

            // 차트가 제대로 렌더링되었는지 확인
            this.$nextTick(() => {
                if (this.barChart) {
                    this.barChart.resize();
                }
            });

            // 리사이즈 이벤트 추가
            window.addEventListener("resize", () => {
                if (this.barChart) {
                    this.barChart.resize();
                }
            });
        },
    },
};
</script>

<style scoped>
/* 분석 모드 스타일 */
.analysis-container {
    height: calc(100% - 41px);
    display: flex;
    flex-direction: column;
    padding: 10px;
    gap: 10px;
}

.analysis-table-container {
    /* flex: 0 0 auto; */
    height: 40%;
    background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
    border-radius: 12px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
    overflow: hidden;
    border: 1px solid rgba(0, 0, 0, 0.05);
}

.analysis-chart-container {
    /* flex: 1; */
    height: 66%;
    /* max-height: 66%; */
    background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
    border-radius: 12px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
    overflow: hidden;
    border: 1px solid rgba(0, 0, 0, 0.05);
    display: flex;
    flex-direction: column;
}

.table-header {
    background: linear-gradient(135deg, #495057 0%, #343a40 100%);
    padding: 8px 12px;
    font-size: 12px;
    font-weight: 700;
    height: 31px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.table-title {
    color: #ffffff;
    font-size: 12px;
    font-weight: 700;
    margin: 0;
    letter-spacing: 0.2px;
}

.table-content {
    padding: 0;
    height: 100%;
}

.table-row {
    display: flex;
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
    height: calc(100% - 31px);
    display: flex;
    align-items: center;
    justify-content: center;
}

.table-row:last-child {
    border-bottom: none;
}

.table-cell {
    flex: 1;
    padding: 5px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    transition: all 0.3s ease;
    height: 100%;
}

.header-cell {
    background: linear-gradient(135deg, #495057 0%, #343a40 100%);
    /* font-size: 12px; */
    font-weight: 700;
    color: #ffffff;
}

.data-cell {
    background: #ffffff;
    position: relative;
}

.data-cell:hover {
    background: rgba(73, 80, 87, 0.02);
    transform: translateY(-1px);
}

.achievement {
    border-right: 1px solid rgba(0, 0, 0, 0.05);
}

.underachievement {
    border-right: 1px solid rgba(0, 0, 0, 0.05);
}

.achievement-count,
.underachievement-count,
.difference-value {
    font-size: 16px;
    font-weight: 800;
    line-height: 1;
    margin-bottom: 1px;
}

.achievement-count {
    color: #4caf50;
}

.underachievement-count {
    color: #f44336;
}

.difference-value {
    color: #2196f3;
}

.difference-positive {
    color: #f44336 !important;
}

.difference-negative {
    color: #4caf50 !important;
}

.difference-neutral {
    color: #2196f3 !important;
}

.achievement-text,
.underachievement-text,
.difference-text {
    font-size: 7px;
    font-weight: 600;
    color: #6c757d;
    letter-spacing: 0.1px;
    text-transform: uppercase;
}

.analysis-chart-container {
    height: 66%;
    background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
    border-radius: 12px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
    overflow: hidden;
    border: 1px solid rgba(0, 0, 0, 0.05);
    display: flex;
    flex-direction: column;
}

.chart-header {
    background: linear-gradient(135deg, #495057 0%, #343a40 100%);
    padding: 8px 12px;
    font-size: 12px;
    font-weight: 700;
    height: 31px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.chart-title {
    color: #ffffff;

    margin: 0;
    letter-spacing: 0.3px;
}

.bar-chart {
    flex: 1;
    /* min-height: 150px; */
    height: 100%;
    width: 100%;
    padding: 5px;
    background: #f8f9fa;
    border-radius: 8px;
    position: relative;
}

.header-row {
    height: 32px;
}
</style>
